<template>
  <div :style="{ position: 'relative' }">
    <Loading :active.sync="loading" :isFullPage="false"></Loading>
    <AddFiles
      :show="showAddFileForm"
      :patId="patId"
      v-on:close="
        () => {
          showAddFileForm = false;
        }
      "
      v-on:addedFile="getFiles"
    />
    <Toast />
    <Dialog
      :visible.sync="showPdfPreview"
      :style="{ width: $isMobile() ? '100vw' : '80vw', height: $isMobile() ? '100vh' : 'auto' }"
      v-bind:class="[{ 'p-dialog-maximized': $isMobile() }]"
      :modal="true"
      :header="previewPdfFile ? getLabel(previewPdfFile) : ''"
      @hide="closePdfPreview"
    >
      <div v-if="previewPdfUrl" class="pdf-modal-body">
        <embed
          id="pdf-modal-viewer"
          :src="previewPdfUrl"
          type="application/pdf"
          width="100%"
          height="100%"
          frameborder="0"
        />
      </div>
      <div v-else-if="showPdfPreview" class="pdf-modal-body p-d-flex p-ai-center p-jc-center">
        <Loading :active.sync="showPdfPreview" :isFullPage="false"></Loading>
      </div>
      <template #footer>
        <Button
          label="Ecrã inteiro"
          icon="pi pi-window-maximize"
          class="p-button-secondary"
          @click="openFullscreen('pdf-modal-viewer')"
        />
        <Button
          label="Fechar"
          icon="pi pi-times"
          class="p-button-outlined p-button-secondary"
          @click="closePdfPreview"
        />
      </template>
    </Dialog>
    <Dialog
      :visible.sync="showEditDialog"
      :style="{ width: $isMobile() ? '100vw' : '48rem' }"
      v-bind:class="[{ 'p-dialog-maximized': $isMobile() }]"
      :modal="true"
      :closable="!editingLoading"
      header="Editar descrição"
    >
      <div v-if="editingFile" class="ocr-dialog-content">
        <div v-if="ocrOptions.length" class="p-field">
          <label class="ocr-section-label">Selecione o serial number</label>
          <div class="ocr-options-list">
            <button
              v-for="(option, index) in ocrOptions"
              :key="`${index}-${option}`"
              type="button"
              class="ocr-option-card"
              :class="{ 'ocr-option-card-active': ocrSelectedOption === option }"
              @click="selectOcrOption(option)"
            >
              {{ option }}
            </button>
          </div>
        </div>

        <div class="ocr-text-card">
          <div class="p-field">
            <label for="file-description" class="ocr-section-label">Descrição</label>
            <Textarea
              id="file-description"
              v-model="editingDescription"
              :maxlength="80"
              rows="8"
              autoResize
              class="p-inputtext-sm ocr-textarea"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          label="Ler imagem"
          icon="pi pi-search"
          class="p-button-info"
          :disabled="!canScanFile(editingFile)"
          :loading="ocrLoading"
          @click="readImageOptions"
        />
        <Button
          label="Guardar"
          icon="pi pi-save"
          class="p-button-success"
          :disabled="editingLoading || !editingDescription.trim()"
          @click="saveDescription"
        />
        <Button
          label="Fechar"
          icon="pi pi-times"
          class="p-button-secondary"
          :disabled="editingLoading || ocrLoading"
          @click="closeEditDialog"
        />
      </template>
    </Dialog>
    <Card class="p-no-shadow-app">
      <template #title>Ficheiros</template>
      <template #content>
        <div class="p-field p-col-12 p-md-10">
          <Button
            label="Adicionar Ficheiro"
            icon="pi pi-plus"
            class="p-button-info p-button-sm p-mr-2"
            @click="showAddFileForm = true"
            v-if="can('addFilesToPat')"
          />
          <Button
            label="Download Ficheiros"
            icon="fa-solid fa-file-zipper"
            class="p-button-primary p-button-sm"
            @click="downloadFiles(patId)"
          />
        </div>
        <div class="p-d-flex p-flex-wrap">
          <div v-for="file in files" :key="file.id">
            <span
              v-if="file.extension.toLowerCase() == 'pdf' && file.file"
              class="pdf-viewer p-d-flex p-ai-center p-flex-column p-mb-4 p-mr-4"
            >
              <span class="label-file p-d-flex p-ai-center">
                {{ getLabel(file) }}
                <Button
                  icon="pi pi-window-maximize"
                  class="p-button-rounded p-button-text p-button-sm p-button-secondary"
                  @click="openPdfPreview(file)"
                />
              </span>
              <embed
                :id="'pdf-viewer_' + file.id"
                :src="`data:application/pdf;base64,${file.file}`"
                type="application/pdf"
                width="100%"
                height="300px"
                frameborder="0"
              />
            </span>
            <span
              v-else
              class="p-d-flex p-ai-center p-flex-column p-mb-4 p-mr-4"
            >
              <span class="p-mb-2 label-file">{{ getLabel(file) }}</span>
              <small class="p-mb-2 label-file">
                <span v-if="file.user">{{ file.user.username }}</span>
                ({{ formatDateTime(file.created_at) }})
              </small>
              <ImagePreview
                v-if="file.file"
                :src="getImageSource(file)"
                height="250"
                fileServiceMethod="getStorageFile"
                class="p-mr-2"
              />

              <AuthenticatedImage
                v-else
                :file="file.filepath"
                :id="file.id"
                height="250"
                fileServiceMethod="getStorageFile"
                class="p-mr-2"
              />

              <div class="p-mt-2 p-d-flex p-flex-wrap">
                <Button
                  v-if="['jpg', 'jpeg', 'png'].includes(file.extension.toLowerCase())"
                  label="Download JPG"
                  icon="pi pi-download"
                  class="p-button-rounded p-button-text p-button-primary"
                  @click.prevent="downloadFile(file)"
                />
                <Button
                  v-if="can('addFilesToPat')"
                  label="Editar descrição"
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-text p-button-info"
                  @click.prevent="openEditDialog(file)"
                />
                <Button
                  v-if="file.extension.toLowerCase() == 'pdf'"
                  icon="pi pi-window-maximize"
                  class="p-button-rounded p-button-text p-button-sm p-button-secondary"
                  @click="openPdfPreview(file)"
                />
              </div>
            </span>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import PatService from "@/services/pat.service";
import filesService from "../../services/files.service";
import AddFiles from "@/components/pats/AddFiles.vue";
import AuthenticatedImage from "@/components/elements/AuthenticatedImage.vue";

export default {
  name: "PatFiles",
  props: {
    patId: {
      type: Number,
      required: true,
    },
  },
  components: { Loading, AddFiles, AuthenticatedImage },
  data() {
    return {
      showAddFileForm: false,
      loading: false,
      files: [],
      showEditDialog: false,
      editingLoading: false,
      editingFile: null,
      editingDescription: "",
      ocrLoading: false,
      ocrOptions: [],
      ocrSelectedOption: null,
      showPdfPreview: false,
      previewPdfFile: null,
      previewPdfUrl: null,
    };
  },
  async mounted() {
    await this.getFiles();
  },
  methods: {
    async getFiles() {
      this.showAddFileForm = false;
      this.loading = true;
      this.files = await PatService.getFiles(this.patId);

      this.loading = false;
    },

    getImageSource(file) {
      return `data:image/${file.extension};base64,${file.file}`;
    },

    limitDescription(value) {
      return (value || "").toString().slice(0, 80);
    },

    canScanFile(file) {
      if (!file || !file.extension) {
        return false;
      }

      return ["jpg", "jpeg", "png", "bmp", "gif", "webp", "tif", "tiff"].includes(
        file.extension.toLowerCase()
      );
    },

    getLabel(file) {
      if (file.resume.toLowerCase().includes("label")) {
        return "Foto da etiqueta";
      } else if (file.resume.toLowerCase().includes("breakdown")) {
        return "Foto da avaria";
      } else if (file.resume.toLowerCase().includes("pop")) {
        return "Foto POP";
      }
      return `Outros | ${file.resume}`;
    },

    openFullscreen(id) {
      const element = document.getElementById(id);
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    },

    async openEditDialog(file) {
      this.editingFile = file;
      this.editingDescription = this.limitDescription(file.resume || "");
      this.showEditDialog = true;
      this.ocrOptions = [];
      this.ocrSelectedOption = null;
    },

    closeEditDialog() {
      this.showEditDialog = false;
      this.editingLoading = false;
      this.editingFile = null;
      this.editingDescription = "";
      this.ocrLoading = false;
      this.ocrOptions = [];
      this.ocrSelectedOption = null;
    },

    selectOcrOption(option) {
      this.ocrSelectedOption = option;
      this.editingDescription = this.limitDescription(option);
    },

    normalizeOcrOption(option) {
      if (typeof option === "string") {
        return option.trim();
      }

      if (!option || typeof option !== "object") {
        return "";
      }

      const candidateKeys = [
        "serial_number",
        "serialNumber",
        "text",
        "description",
        "label",
        "value",
        "content",
        "title",
        "name",
      ];

      for (const key of candidateKeys) {
        if (typeof option[key] === "string" && option[key].trim()) {
          return option[key].trim();
        }
      }

      return "";
    },

    extractOcrOptions(payload) {
      if (!payload) {
        return [];
      }

      const optionKeys = [
        "options",
        "choices",
        "results",
        "suggestions",
        "texts",
        "candidates",
        "descriptions",
        "data",
      ];

      const sourceArrays = [];

      if (Array.isArray(payload)) {
        sourceArrays.push(payload);
      }

      if (typeof payload === "object") {
        optionKeys.forEach(key => {
          if (Array.isArray(payload[key])) {
            sourceArrays.push(payload[key]);
          }
        });
      }

      const normalizedOptions = sourceArrays.reduce((accumulator, options) => {
        const values = options
          .map(option => this.normalizeOcrOption(option))
          .filter(Boolean);

        return accumulator.concat(values);
      }, []);

      return [...new Set(normalizedOptions)];
    },

    hasValidOcrResult(response) {
      const status = response?.data?.status;
      const options = this.extractOcrOptions(response?.data);

      if (status && status !== "success" && options.length === 0) {
        return false;
      }

      return true;
    },

    dataUrlToBlob(dataUrl) {
      const parts = dataUrl.split(",");
      const mime = parts[0].match(/:(.*?);/)[1];
      const binary = atob(parts[1]);
      const array = new Uint8Array(binary.length);

      for (let i = 0; i < binary.length; i += 1) {
        array[i] = binary.charCodeAt(i);
      }

      return new Blob([array], { type: mime });
    },

    async readImageOptions() {
      if (!this.editingFile || !this.canScanFile(this.editingFile)) {
        return;
      }

      this.ocrLoading = true;

      try {
        const formData = new FormData();

        if (this.editingFile.file) {
          const blob = this.dataUrlToBlob(this.getImageSource(this.editingFile));
          formData.append("file", blob, `${this.editingFile.fname || "image"}.${this.editingFile.extension}`);
        } else {
          const imageBlob = await filesService.getStorageFile(this.editingFile.filepath);
          if (!imageBlob) {
            throw new Error("Missing image");
          }
          formData.append(
            "file",
            imageBlob,
            `${this.editingFile.fname || "image"}.${this.editingFile.extension}`
          );
        }

        const response = await filesService.scanFileOcr(formData);
        const recognizedOptions = this.extractOcrOptions(response?.data);
        const recognizedText = this.hasValidOcrResult(response) && response?.text
          ? response.text.trim()
          : "";

        if (!recognizedText && !recognizedOptions.length) {
          this.$toast.add({
            severity: "warn",
            summary: "OCR",
            detail: "Não foi possível identificar texto na imagem.",
            life: 3000,
          });
          return;
        }

        if (recognizedOptions.length) {
          this.ocrOptions = recognizedOptions;
          this.ocrSelectedOption = recognizedOptions[0];
          this.editingDescription = this.limitDescription(recognizedOptions[0]);
          return;
        }

        this.editingDescription = this.limitDescription(recognizedText);
        this.$toast.add({
          severity: "success",
          summary: "OCR",
          detail: "Descrição preenchida automaticamente.",
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Ocorreu um erro ao ler a imagem com OCR.",
          life: 3000,
        });
      } finally {
        this.ocrLoading = false;
      }
    },

    async saveDescription() {
      if (!this.editingFile || !this.editingDescription.trim()) {
        return;
      }

      this.editingLoading = true;

      try {
        const response = await filesService.updateDescription(
          this.editingFile.id,
          this.limitDescription(this.editingDescription.trim())
        );

        if (!response?.updated) {
          throw new Error("Update failed");
        }

        this.$toast.add({
          severity: "success",
          summary: "Sucesso",
          detail: "Descrição atualizada com sucesso.",
          life: 3000,
        });

        await this.getFiles();
        this.closeEditDialog();
      } catch (error) {
        this.editingLoading = false;
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível atualizar a descrição.",
          life: 3000,
        });
      }
    },

    async downloadFiles(patId) {
      this.loading = true;
      let zip = await PatService.downloadAllFiles(patId);
      this.loading = false;
      const url = window.URL.createObjectURL(new Blob([zip]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PAT_${patId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    async downloadFile(file) {
      try {
        let downloadUrl;
        let isBlobUrl = false;

        if (file.file) {
          downloadUrl = this.getImageSource(file);
        } else {
          downloadUrl = await this.getFileSource(file);
          isBlobUrl = true;
        }

        if (!downloadUrl) {
          throw new Error('Failed to generate download URL');
        }

        const filename = `PAT_${this.patId}_${file.resume}`.replace(/\s+/g, '_');
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${filename}.jpg`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (isBlobUrl) {
          setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
        }

      } catch (error) {
        console.error('Download failed:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Falha ao fazer download do ficheiro',
          life: 3000,
        });
      }
    },

    async getFileSource(file) {
      try {
        const imageBlob = await filesService.getStorageFile(file.filepath);
        if (imageBlob) {
          return URL.createObjectURL(imageBlob);
        }
      } catch (error) {
        console.error("Failed to load authenticated image:", error);
      }
    },

    async openPdfPreview(file) {
      this.previewPdfFile = file;
      this.showPdfPreview = true;
      this.previewPdfUrl = null;

      if (file.file) {
        this.previewPdfUrl = `data:application/pdf;base64,${file.file}`;
      } else if (file.filepath) {
        try {
          const blob = await filesService.getStorageFile(file.filepath);
          if (blob) {
            this.previewPdfUrl = URL.createObjectURL(blob);
          }
        } catch (error) {
          this.$toast.add({
            severity: "error",
            summary: "Erro",
            detail: "Não foi possível carregar o PDF.",
            life: 3000,
          });
        }
      }
    },

    closePdfPreview() {
      this.showPdfPreview = false;
      this.previewPdfFile = null;
      if (this.previewPdfUrl && this.previewPdfUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.previewPdfUrl);
      }
      this.previewPdfUrl = null;
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-PT') + ' ' + date.toLocaleTimeString('pt-PT');
    },
  }
};
</script>

<style scoped>
.label-file {
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
}

.ocr-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ocr-text-card {
  border: 1px solid #d7dee7;
  border-radius: 10px;
  background: #f8fafc;
  padding: 0.875rem;
}

.ocr-section-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #243447;
}

.ocr-options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  max-height: 11rem;
  overflow-y: auto;
}

.ocr-option-card {
  appearance: none;
  border: 1px solid #c8d3df;
  background: #ffffff;
  color: #243447;
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.2;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ocr-option-card:hover,
.ocr-option-card:focus {
  border-color: #3b82f6;
  outline: none;
}

.ocr-option-card-active {
  background: #eaf3ff;
  border-color: #3b82f6;
  color: #0f4c81;
}

.ocr-textarea {
  width: 100%;
  min-height: 9rem;
}

.pdf-modal-body {
  width: 100%;
  height: 75vh;
}
</style>
